"use client";

import React, { useEffect } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/lib/auth";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireRole?: "nurse" | "facility" | "admin";
  requireRoles?: Array<"nurse" | "facility" | "admin">;
  requireVerified?: boolean;
  redirectTo?: string;
}

export function ProtectedRoute({
  children,
  requireRole,
  requireRoles,
  requireVerified = true,
  redirectTo = "/login",
}: ProtectedRouteProps) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const router = useRouter();

  useEffect(() => {
    const allowedRoles = requireRoles ?? (requireRole ? [requireRole] : null);

    if (!isLoading) {
      if (!isAuthenticated) {
        // Not authenticated, redirect to login
        router.push(redirectTo);
      } else if (requireVerified && user && !user.is_verified) {
        router.push("/verify-email");
      } else if (requireRole && user?.role !== requireRole) {
        // Authenticated but wrong role, redirect to appropriate page
        if (user?.role === "nurse") {
          router.push("/jobs");
        } else if (user?.role === "facility") {
          router.push("/facility-dashboard");
        } else if (user?.role === "admin") {
          router.push("/admin/dashboard");
        } else {
          router.push("/");
        }
      } else if (allowedRoles && user && !allowedRoles.includes(user.role)) {
        if (user.role === "nurse") {
          router.push("/jobs");
        } else if (user.role === "facility") {
          router.push("/facility-dashboard");
        } else if (user.role === "admin") {
          router.push("/admin/dashboard");
        } else {
          router.push("/");
        }
      }
    }
  }, [
    isLoading,
    isAuthenticated,
    user,
    requireRole,
    requireRoles,
    requireVerified,
    redirectTo,
    router,
  ]);

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[var(--primary)] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[var(--gray-600)]">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render children if not authenticated or wrong role
  const allowedRoles = requireRoles ?? (requireRole ? [requireRole] : null);
  if (
    !isAuthenticated ||
    (requireVerified && user && !user.is_verified) ||
    (allowedRoles && user && !allowedRoles.includes(user.role))
  ) {
    return null;
  }

  return <>{children}</>;
}
